import { useCallback, useEffect, useRef, useState } from "react";
import { socketService } from "@/sockets/socketService";
import { AI_VOICE_AUDIO_IN, AI_VOICE_MEDIA_STATE, AI_VOICE_START, AI_VOICE_STOP, AI_VOICE_TEXT_IN, SocketEvents } from "@/sockets/socketEvents";
import type { TelecallerHello, TelecallerOutcome, TelecallerStartOptions } from "./telecaller.types";
import { createPcmPlayer, startMicCapture, type MicCaptureHandle, type PcmPlayerHandle } from "./audio";
import { clampTempo, readTunedTempo, writeTunedTempo, type VoicePace } from "./tempo";
import type { AIChart } from "../types/ai.types";

// ---------------------------------------------------------------------------
// useLiveVoice — the state machine behind the voice-call overlay.
//
//   start(language) → mic on, `ai:voice:start` → server opens Gemini Live
//   mic frames    → `ai:voice:audio` (binary)
//   server audio  → PcmPlayer (gapless), transcripts → captions,
//   state events  → listening / thinking / speaking; `interrupted` flushes
//                   the player (barge-in), `ended` tears everything down.
//   media         → `ai:voice:media` pushes a recording card. While it PLAYS
//                   the mic is speech-gated (audio.ts "vad"): nothing reaches
//                   the model, but the user starting to talk pauses the
//                   recording and reopens the mic — so "stop" always works.
//                   The server answers stop / pause / resume through
//                   `ai:voice:media:control` (the `control_playback` tool or
//                   its spoken-stop fast path); we report the card's state
//                   through `ai:voice:media:state`.
//   chart         → `ai:voice:chart { chart }` pushes a chart card (run_report /
//                   render_chart). Each push replaces the previous card; it is
//                   dismissable and cleared with the media when the call ends.
//   telecaller    → start(language, {mode:"telecaller", leadId, …}) opens the
//                   SAME pipe with the AI-telecaller persona on the server;
//                   `ai:voice:telecaller` brings the lead card, `ai:voice:outcome`
//                   the note/follow-up the AI logged (or the fallback note).
// ---------------------------------------------------------------------------

export type CallStatus = "idle" | "connecting" | "listening" | "thinking" | "speaking" | "ended" | "error";

export interface TranscriptLine {
  id: number;
  role: "user" | "assistant";
  text: string;
  interrupted?: boolean;
  typed?: boolean;
}

export interface VoiceMedia {
  kind: "recording";
  /** Monotonic per call — keys the <audio> so replaying the SAME callId remounts it. */
  seq: number;
  callId: number;
  url: string;
  title: string;
  durationSec: number | null;
  leadName: string | null;
  callerName: string | null;
  /** The user started talking — playback paused, waiting for continue / stop. */
  pausedBySpeech?: boolean;
  /** Played to the end; the card stays so they can replay it. */
  ended?: boolean;
}

type MediaControlAction = "stop" | "pause" | "resume";

export interface LiveVoiceState {
  status: CallStatus;
  /** "telecaller" while an AI-telecaller call is open; "assistant" otherwise. */
  mode: "assistant" | "telecaller";
  /** Telecaller: the lead brief the server built for this call. */
  telecaller: TelecallerHello | null;
  /** Telecaller: the outcome the AI logged (note / follow-up), or the fallback. */
  outcome: TelecallerOutcome | null;
  /** Telecaller: the server refused to start (outside hours / do-not-call) — the code lets the sheet offer an override. */
  errorCode: string | null;
  /** Playback tempo of the AI voice, pitch preserved (1 = as generated; the telecaller's brisk default is 1.15). */
  tempo: number;
  /** A recording pushed by play_call_recording; the overlay plays it. */
  media: VoiceMedia | null;
  /** The latest chart pushed over `ai:voice:chart`; the overlay draws it. */
  chart: AIChart | null;
  /** Live (not yet final) captions. */
  liveUser: string;
  liveAssistant: string;
  transcript: TranscriptLine[];
  toolActivity: string | null;
  error: string | null;
  muted: boolean;
  endedReason: string | null;
}

const READY_TIMEOUT_MS = 12_000;
/** "thinking" with no server event for this long → back to listening + a hint. */
const THINKING_STALL_MS = 45_000;
const STALL_POLL_MS = 5_000;

export function useLiveVoice() {
  const [state, setState] = useState<LiveVoiceState>({
    status: "idle",
    mode: "assistant",
    telecaller: null,
    outcome: null,
    errorCode: null,
    liveUser: "",
    liveAssistant: "",
    transcript: [],
    toolActivity: null,
    error: null,
    muted: false,
    endedReason: null,
    media: null,
    chart: null,
    tempo: 1,
  });
  const micRef = useRef<MicCaptureHandle | null>(null);
  const playerRef = useRef<PcmPlayerHandle | null>(null);
  const unsubsRef = useRef<Array<() => void>>([]);
  const readyTimerRef = useRef<number | null>(null);
  const lineIdRef = useRef(0);
  const statusRef = useRef<CallStatus>("idle");
  const speakingSettleRef = useRef<number | null>(null);
  const languageRef = useRef<"ml" | "en">("ml");
  const lastEventAtRef = useRef<number>(0);
  const stallTimerRef = useRef<number | null>(null);
  /** The media card's <audio>, registered by the overlay (null when no card). */
  const mediaElRef = useRef<HTMLAudioElement | null>(null);
  const pausedBySpeechRef = useRef(false);
  /** Telecaller: the pace in force, so a tuned tempo is remembered under it. */
  const paceRef = useRef<VoicePace | undefined>(undefined);

  const setStatus = useCallback((status: CallStatus) => {
    statusRef.current = status;
    setState((s) => (s.status === status ? s : { ...s, status }));
  }, []);

  const teardownAudio = useCallback(() => {
    micRef.current?.stop();
    micRef.current = null;
    playerRef.current?.stop();
    playerRef.current = null;
  }, []);

  const unsubscribeAll = useCallback(() => {
    unsubsRef.current.forEach((u) => u());
    unsubsRef.current = [];
    if (readyTimerRef.current) {
      window.clearTimeout(readyTimerRef.current);
      readyTimerRef.current = null;
    }
    if (speakingSettleRef.current) {
      window.clearTimeout(speakingSettleRef.current);
      speakingSettleRef.current = null;
    }
    if (stallTimerRef.current) {
      window.clearInterval(stallTimerRef.current);
      stallTimerRef.current = null;
    }
  }, []);

const mediaSeqRef = useRef(0);

  /** The call is over (or failed): silence any recording still open and drop
   *  the chart card. No media:state emit — there is no run to tell. */
  const clearMediaSilently = useCallback(() => {
    pausedBySpeechRef.current = false;
    const el = mediaElRef.current;
    if (el && !el.paused) {
      try {
        el.pause();
      } catch {
        /* already gone */
      }
    }
    setState((s) => (s.media || s.chart ? { ...s, media: null, chart: null } : s));
  }, []);

  /** The user closed the chart card (×). Purely local — the run is unaffected. */
  const dismissChart = useCallback(() => {
    setState((s) => (s.chart ? { ...s, chart: null } : s));
  }, []);

  const stop = useCallback(
    (reason = "stopped") => {
      socketService.emit(AI_VOICE_STOP);
      unsubscribeAll();
      teardownAudio();
      clearMediaSilently();
      statusRef.current = "ended";
      setState((s) => ({ ...s, status: "ended", liveUser: "", liveAssistant: "", toolActivity: null, endedReason: reason }));
    },
    [teardownAudio, unsubscribeAll, clearMediaSilently],
  );

  // ---- Recording card (media) -------------------------------------------

  /** `ai:voice:media:state {active, playing, pausedBySpeech?}` → server. */
  const emitMediaState = useCallback((active: boolean, playing: boolean, pausedBySpeech?: boolean) => {
    socketService.emit(AI_VOICE_MEDIA_STATE, pausedBySpeech ? { active, playing, pausedBySpeech: true } : { active, playing });
  }, []);

  const registerMediaElement = useCallback((el: HTMLAudioElement | null) => {
    mediaElRef.current = el;
  }, []);


  /** The local VAD heard the user while the recording played: pause it, reopen the mic. */
  const onSpeech = useCallback(() => {
    const el = mediaElRef.current;
    if (!el) return;
    pausedBySpeechRef.current = true;
    if (!el.paused) el.pause();
    micRef.current?.setGate("open");
    setState((s) => (s.media ? { ...s, media: { ...s.media, pausedBySpeech: true } } : s));
    emitMediaState(true, false, true);
  }, [emitMediaState]);

  /** <audio onPlay> — gate the mic on speech; the server flushes Gemini's audio stream. */
  const onMediaPlay = useCallback(() => {
    if (!mediaElRef.current) return;
    pausedBySpeechRef.current = false;
    setState((s) => (s.media && (s.media.pausedBySpeech || s.media.ended) ? { ...s, media: { ...s.media, pausedBySpeech: false, ended: false } } : s));
    micRef.current?.setGate("vad", onSpeech);
    emitMediaState(true, true);
  }, [emitMediaState, onSpeech]);

  /** <audio onPause> — the user pressed pause, or we paused it (speech / control). */
  const onMediaPause = useCallback(() => {
    if (!mediaElRef.current) return;
    micRef.current?.setGate("open");
    emitMediaState(true, false, pausedBySpeechRef.current);
  }, [emitMediaState]);

  /** <audio onEnded> — keep the card so they can replay; mic fully open. */
  const onMediaEnded = useCallback(() => {
    pausedBySpeechRef.current = false;
    micRef.current?.setGate("open");
    setState((s) => (s.media ? { ...s, media: { ...s.media, pausedBySpeech: false, ended: true } } : s));
    emitMediaState(false, false);
  }, [emitMediaState]);

  const dismissMedia = useCallback(() => {
    pausedBySpeechRef.current = false;
    micRef.current?.setGate("open");
    setState((s) => (s.media ? { ...s, media: null } : s));
    emitMediaState(false, false);
  }, [emitMediaState]);

  // ---- Call lifecycle -----------------------------------------------------

  const start = useCallback(
    async (language: "ml" | "en", options?: TelecallerStartOptions) => {
      if (statusRef.current === "connecting" || statusRef.current === "listening" || statusRef.current === "speaking" || statusRef.current === "thinking") return;
      const mode = options?.mode === "telecaller" ? "telecaller" : "assistant";
      setState({
        status: "connecting",
        mode,
        telecaller: null,
        outcome: null,
        errorCode: null,
        liveUser: "",
        liveAssistant: "",
        transcript: [],
        toolActivity: null,
        error: null,
        muted: false,
        endedReason: null,
        media: null,
        chart: null,
        tempo: 1,
      });
      statusRef.current = "connecting";
      pausedBySpeechRef.current = false;
      paceRef.current = undefined;

      // Audio first — the permission prompt must come from the user's tap.
      try {
        playerRef.current = createPcmPlayer();
        micRef.current = await startMicCapture((pcm) => {
          if (statusRef.current === "listening" || statusRef.current === "speaking" || statusRef.current === "thinking") {
            socketService.emit(AI_VOICE_AUDIO_IN, pcm);
          }
        });
      } catch (err) {
        teardownAudio();
        statusRef.current = "error";
        setState((s) => ({ ...s, status: "error", error: (err as Error)?.message?.includes("Permission") || (err as Error)?.name === "NotAllowedError" ? "Microphone access was blocked. Allow the mic in your browser and try again." : (err as Error)?.message || "Could not start the microphone." }));
        return;
      }

      // socketService's subscriber callback is untyped; each handler below narrows its own payload.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sub = (event: SocketEvents, cb: (data: any) => void) =>
        unsubsRef.current.push(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          socketService.subscribe(event, (data: any) => {
            lastEventAtRef.current = Date.now();
            cb(data);
          }),
        );
      languageRef.current = language;
      lastEventAtRef.current = Date.now();
      // Watchdog: "thinking" is only ever a transient state (a tool running,
      // the model composing). If nothing at all arrives for a long while,
      // flip back to listening and say so — the user can just ask again.
      stallTimerRef.current = window.setInterval(() => {
        if (statusRef.current !== "thinking") return;
        if (Date.now() - lastEventAtRef.current < THINKING_STALL_MS) return;
        setStatus("listening");
        setState((s) => ({
          ...s,
          toolActivity:
            languageRef.current === "ml"
              ? "മറുപടി വന്നില്ല — ഒന്നുകൂടി ചോദിക്കൂ, അല്ലെങ്കിൽ ടൈപ്പ് ചെയ്യൂ."
              : "No reply came — ask again, or type it.",
        }));
      }, STALL_POLL_MS);

      sub(SocketEvents.AI_VOICE_READY, () => {
        if (readyTimerRef.current) {
          window.clearTimeout(readyTimerRef.current);
          readyTimerRef.current = null;
        }
        setStatus("listening");
      });
      sub(SocketEvents.AI_VOICE_AUDIO, (data: ArrayBuffer | Uint8Array) => {
        // socket.io delivers binary as ArrayBuffer (browser) — copy views into a plain ArrayBuffer.
        const buf: ArrayBuffer | null =
          data instanceof ArrayBuffer ? data : data instanceof Uint8Array ? (data.slice().buffer as ArrayBuffer) : null;
        if (!buf) return;
        playerRef.current?.enqueue(buf);
        setStatus("speaking");
        // Fall back to listening once the queued audio has drained.
        if (speakingSettleRef.current) window.clearTimeout(speakingSettleRef.current);
        const settle = () => {
          if (playerRef.current?.isPlaying()) {
            speakingSettleRef.current = window.setTimeout(settle, 150);
            return;
          }
          speakingSettleRef.current = null;
          if (statusRef.current === "speaking") setStatus("listening");
        };
        speakingSettleRef.current = window.setTimeout(settle, 300);
      });
      sub(SocketEvents.AI_VOICE_TRANSCRIPT, (t: { role: "user" | "assistant"; text: string; final?: boolean; interrupted?: boolean; typed?: boolean }) => {
        if (!t?.text) return;
        setState((s) => {
          if (t.final) {
            const line: TranscriptLine = { id: ++lineIdRef.current, role: t.role, text: t.text, interrupted: t.interrupted, typed: t.typed };
            return {
              ...s,
              transcript: [...s.transcript.slice(-30), line],
              liveUser: t.role === "user" ? "" : s.liveUser,
              liveAssistant: t.role === "assistant" ? "" : s.liveAssistant,
            };
          }
          return t.role === "user" ? { ...s, liveUser: t.text } : { ...s, liveAssistant: t.text };
        });
      });
      sub(SocketEvents.AI_VOICE_STATE, (p: { state: string; stalled?: boolean }) => {
        switch (p?.state) {
          case "listening":
            if (!playerRef.current?.isPlaying()) setStatus("listening");
            setState((s) =>
              p?.stalled
                ? {
                    ...s,
                    toolActivity:
                      languageRef.current === "ml"
                        ? "മറുപടി വന്നില്ല — ഒന്നുകൂടി ചോദിക്കൂ."
                        : "The assistant went quiet — ask again.",
                  }
                : s.toolActivity
                  ? { ...s, toolActivity: null }
                  : s,
            );
            break;
          case "thinking":
            setStatus("thinking");
            break;
          case "speaking":
            setStatus("speaking");
            break;
          case "interrupted":
            playerRef.current?.flush();
            setStatus("listening");
            break;
          case "ending":
            setState((s) => ({ ...s, toolActivity: "Call is about to end — say the last thing you need." }));
            break;
        }
      });
      sub(SocketEvents.AI_VOICE_TOOL, (p: { name: string; status: string; slow?: boolean }) => {
        setState((s) => ({
          ...s,
          toolActivity:
            p?.status === "running"
              ? p?.slow
                ? languageRef.current === "ml"
                  ? "ഇപ്പോഴും നോക്കുന്നു… കുറച്ച് സമയം എടുക്കും."
                  : "Still working on it… this one takes a moment."
                : describeTool(p.name)
              : p?.status === "error"
                ? "Could not fetch that — I'll say so."
                : s.toolActivity,
        }));
      });
      sub(SocketEvents.AI_VOICE_MEDIA, (m: VoiceMedia) => {
        if (!m?.url) return;
        // Pause the assistant's own audio so the recording is heard cleanly.
        playerRef.current?.flush();
        // The mic stays open until the element actually starts playing (onMediaPlay).
        pausedBySpeechRef.current = false;
        micRef.current?.setGate("open");
        const seq = ++mediaSeqRef.current;
        setState((s) => ({ ...s, media: { ...m, seq, pausedBySpeech: false, ended: false } }));
      });
      sub(SocketEvents.AI_VOICE_CHART, (p: { chart?: AIChart }) => {
        const chart = p?.chart;
        if (!chart || !Array.isArray(chart.categories) || !Array.isArray(chart.series)) return;
        // Each push replaces the previous card — the overlay shows one chart.
        setState((s) => ({ ...s, chart }));
      });
      sub(SocketEvents.AI_VOICE_MEDIA_CONTROL, (p: { action?: MediaControlAction }) => {
        const el = mediaElRef.current;
        switch (p?.action) {
          case "stop":
            dismissMedia();
            break;
          case "pause":
            if (el && !el.paused) el.pause();
            break;
          case "resume":
            // onPlay re-arms the speech gate.
            if (el) void el.play().catch(() => undefined);
            break;
        }
      });
      sub(SocketEvents.AI_VOICE_TELECALLER, (p: TelecallerHello) => {
        if (!p?.lead) return;
        // Tempo: Gemini has no speaking-rate setting, so the pace the server
        // resolved becomes a pitch-preserving stretch in the player (tempo.ts),
        // in place before the opening line arrives.
        const pace: VoicePace = p.pace ?? "brisk";
        paceRef.current = pace;
        const tempo = readTunedTempo(pace);
        playerRef.current?.setRate(tempo);
        setState((s) => ({ ...s, telecaller: p, tempo }));
      });
      sub(SocketEvents.AI_VOICE_OUTCOME, (p: TelecallerOutcome) => {
        if (!p?.disposition) return;
        setState((s) => ({ ...s, outcome: p }));
      });
      // The custom voice (Sarvam / ElevenLabs) dropped mid-call: the server
      // switched to Gemini's own voice so the line never goes silent — say so.
      sub(SocketEvents.AI_VOICE_ENGINE, (p: { engine?: string; fallback?: boolean; message?: string }) => {
        if (!p?.fallback) return;
        setState((s) => ({
          ...s,
          toolActivity:
            languageRef.current === "ml"
              ? "Custom voice നിന്നുപോയി — ബാക്കി Gemini voice-ൽ തുടരുന്നു."
              : "The custom voice dropped — continuing in Gemini's voice.",
        }));
      });
      sub(SocketEvents.AI_VOICE_ERROR, (p: { message?: string; code?: string }) => {
        const message = p?.message || "Voice call error.";
        unsubscribeAll();
        teardownAudio();
        clearMediaSilently();
        statusRef.current = "error";
        setState((s) => ({ ...s, status: "error", error: message, errorCode: p?.code ?? null }));
      });
      sub(SocketEvents.AI_VOICE_ENDED, (p: { reason?: string }) => {
        if (statusRef.current === "ended" || statusRef.current === "error") return;
        // The server ends the previous run silently on restart/stop; a stray
        // one (older server) must not kill the call that just started.
        if (p?.reason === "restarted" || p?.reason === "stopped") return;
        unsubscribeAll();
        teardownAudio();
        clearMediaSilently();
        statusRef.current = "ended";
        setState((s) => ({ ...s, status: "ended", liveUser: "", liveAssistant: "", toolActivity: null, endedReason: p?.reason ?? "ended" }));
      });

      readyTimerRef.current = window.setTimeout(() => {
        if (statusRef.current !== "connecting") return;
        unsubscribeAll();
        teardownAudio();
        clearMediaSilently();
        statusRef.current = "error";
        setState((s) => ({ ...s, status: "error", error: "Could not connect the voice call. Check that you are online and try again." }));
      }, READY_TIMEOUT_MS);

      socketService.emit(
        AI_VOICE_START,
        mode === "telecaller" && options?.mode === "telecaller"
          ? {
              language,
              mode: "telecaller",
              leadId: options.leadId,
              objective: options.objective,
              brief: options.brief,
              agentName: options.agentName,
              voice: options.voice,
              discloseAI: options.discloseAI,
              allowOutsideHours: options.allowOutsideHours,
              allowDoNotCall: options.allowDoNotCall,
              practice: options.practice,
              dialect: options.dialect,
              greeting: options.greeting,
              companyName: options.companyName,
              companySayAs: options.companySayAs,
              pace: options.pace,
              engine: options.engine,
              engineVoice: options.engineVoice,
            }
          : { language },
      );
    },
    [dismissMedia, setStatus, teardownAudio, unsubscribeAll, clearMediaSilently],
  );

  const toggleMute = useCallback(() => {
    setState((s) => {
      const muted = !s.muted;
      micRef.current?.setMuted(muted);
      return { ...s, muted };
    });
  }, []);

  const sendText = useCallback((text: string) => {
    const t = text.trim();
    if (!t) return;
    playerRef.current?.flush();
    socketService.emit(AI_VOICE_TEXT_IN, { text: t });
    setStatus("thinking");
  }, [setStatus]);

  /** Playback tempo (pitch preserved), live during the call; remembered per pace for the telecaller. */
  const setTempo = useCallback((rate: number) => {
    const r = clampTempo(rate);
    playerRef.current?.setRate(r);
    if (paceRef.current) writeTunedTempo(paceRef.current, r);
    setState((s) => (s.tempo === r ? s : { ...s, tempo: r }));
  }, []);

  /** Live levels for the orb — read inside a rAF loop, never via state. */
  const levels = useCallback(() => ({ mic: micRef.current?.level() ?? 0, out: playerRef.current?.level() ?? 0 }), []);

  useEffect(() => () => {
    unsubscribeAll();
    teardownAudio();
    if (statusRef.current !== "idle" && statusRef.current !== "ended") socketService.emit(AI_VOICE_STOP);
  }, [teardownAudio, unsubscribeAll]);

  return { state, start, stop, toggleMute, sendText, setTempo, levels, dismissMedia, dismissChart, registerMediaElement, onMediaPlay, onMediaPause, onMediaEnded };
}

function describeTool(name: string): string {
  const map: Record<string, string> = {
    describe_users: "Finding that person…",
    user_day_report: "Pulling their day…",
    team_day_report: "Checking the team…",
    user_scorecard: "Reading the scorecard…",
    user_activity_timeline: "Reading their timeline…",
    report_digest: "Building the report…",
    report_section: "Opening that report…",
    kpi_summary: "Getting the headline numbers…",
    run_report: "Running the report…",
    render_chart: "Drawing the chart…",
    count_followups: "Counting follow-ups…",
    count_leads: "Counting leads…",
    nudge_user: "Preparing the nudge…",
    play_call_recording: "Opening the recording…",
    control_playback: "Applying that to the recording…",
    telecaller_log_outcome: "Saving the call note…",
    telecaller_do_not_call: "Marking do-not-call…",
    telecaller_end_call: "Wrapping up…",
    search_universities: "Checking our universities…",
    search_courses: "Checking our courses…",
  };
  return map[name] ?? "Checking the CRM…";
}
